// Scroll a tour target into a comfortable position inside the real scroll
// container (#root, see src/styles.css). The window itself does not scroll
// in this app, so window.scrollTo is a no-op — we must move #root.

import { withTemporaryUnlock } from "./scrollLock";

function getScrollContainer(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.getElementById("root");
}

function findTarget(selector: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const direct = document.querySelector(selector) as HTMLElement | null;
  if (direct) return direct;
  const safe = (window as any).CSS?.escape ? (window as any).CSS.escape(selector) : selector;
  return document.querySelector(`[data-tour="${safe}"]`) as HTMLElement | null;
}

export const SCROLL_DURATION_MS = 320;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function smoothScrollTo(container: HTMLElement, targetY: number, duration = SCROLL_DURATION_MS) {
  const startY = container.scrollTop;
  const delta = targetY - startY;
  if (Math.abs(delta) < 1) return;
  const supportsSmooth = "scrollBehavior" in document.documentElement.style;
  if (supportsSmooth) {
    try {
      container.scrollTo({ top: targetY, behavior: "smooth" });
      return;
    } catch { /* fall through to rAF tween */ }
  }
  const start = performance.now();
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / duration);
    container.scrollTop = startY + delta * easeOutCubic(t);
    if (t < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function scrollTargetIntoView(selector: string) {
  if (typeof window === "undefined") return;
  const el = findTarget(selector);
  if (!el) return;
  const container = getScrollContainer();
  if (!container) return;

  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const vh = containerRect.height || window.innerHeight;

  // Place target ~30% from the top of the container, clear of the tour bar.
  const desiredViewportY = Math.max(96, vh * 0.3);
  const currentTopInContainer = elRect.top - containerRect.top;
  const delta = currentTopInContainer - desiredViewportY;
  if (Math.abs(delta) < 4) return;

  const targetY = Math.max(0, container.scrollTop + delta);
  withTemporaryUnlock(() => {
    smoothScrollTo(container, targetY);
  });
}
